/* eslint-disable max-params */
import type { ChatAppRequest, ChatAppResponse } from "../models"

import { logger } from "@/lib/logger"

import { AuthMode, getAuthMode, refreshEasyAuthToken } from "../auth-api"
import { WebSocketClient } from "../web-socket-client"
import { BACKEND_URI, fetchWithAuthRetry, getHeaders } from "./_shared"

/**
 * STREAMING: Translates chat via WebSocket (real-time streaming)
 * Note: This is a WebSocket operation, not a standard query/mutation
 * @param request - Chat request data
 * @param files - Optional files to include
 * @param idToken - Authentication token
 * @param signal - Abort signal for cancellation
 * @returns Promise resolving to chat response
 * @category Streaming
 */
export async function translateChatApi(
  request: ChatAppRequest,
  files: File[] | undefined,
  idToken: string | undefined,
  signal: AbortSignal
): Promise<ChatAppResponse> {
  if (!idToken) {
    return Promise.reject(new Error("Missing authentication token."))
  }

  const wsClient = new WebSocketClient({
    url: `${BACKEND_URI}/ws/translate-chat`,
    idToken,
    requestData: request,
    files,
    signal,
    onHeartbeat: (timestamp: number) => {
      logger.debug(`Heartbeat received at ${new Date(timestamp * 1000).toISOString()}`)
    },
    onAuthRefresh: async () => {
      logger.info("Attempting to refresh token for WebSocket connection")
      const authMode = getAuthMode()

      if (authMode === AuthMode.EASYAUTH) {
        const refreshed = await refreshEasyAuthToken()
        if (refreshed) {
          logger.info("Successfully refreshed token for WebSocket connection")
          return idToken // Return the same token as EasyAuth handles it via cookies
        }
      }

      return undefined // Refresh failed
    },
  })

  return wsClient.getPromise()
}

/**
 * STREAMING: Support chat via HTTP POST (non-streaming)
 * @param request - Chat request data
 * @param files - Optional files to include
 * @param idToken - Authentication token
 * @param signal - Abort signal for cancellation
 * @returns Response promise
 * @category Streaming
 */
export async function supportChatApi(
  request: ChatAppRequest,
  files: File[] | undefined,
  idToken: string | undefined,
  signal: AbortSignal
): Promise<Response> {
  const formData = new FormData()

  // Append the original request data as a JSON string under a specific key
  formData.append("request", JSON.stringify(request))

  // Append files if any
  if (files) {
    Array.from(files).forEach((file) => {
      formData.append("files", file)
    })
  }

  return fetchWithAuthRetry(
    `${BACKEND_URI}/support-chat`,
    {
      method: "POST",
      headers: getHeaders(idToken, true),
      body: formData,
      signal,
    },
    idToken
  )
}

/**
 * STREAMING: Video insight chat via WebSocket (real-time streaming)
 * Note: This is a WebSocket operation, not a standard query/mutation
 * @param request - Chat request data
 * @param files - Optional files to include
 * @param idToken - Authentication token
 * @param signal - Abort signal for cancellation
 * @returns Promise resolving to chat response
 * @category Streaming
 */
export async function videoInsightApi(
  request: ChatAppRequest,
  files: File[] | undefined,
  idToken: string | undefined,
  signal: AbortSignal
): Promise<ChatAppResponse> {
  if (!idToken) {
    return Promise.reject(new Error("Missing authentication token."))
  }

  files = []

  const wsClient = new WebSocketClient({
    url: `${BACKEND_URI}/ws/video-insight`,
    idToken,
    requestData: request,
    files,
    signal,
    onHeartbeat: (timestamp: number) => {
      logger.debug(`Heartbeat received at ${new Date(timestamp * 1000).toISOString()}`)
    },
    onAuthRefresh: async () => {
      logger.info("Attempting to refresh token for WebSocket connection")
      const authMode = getAuthMode()

      if (authMode === AuthMode.EASYAUTH) {
        const refreshed = await refreshEasyAuthToken()
        if (refreshed) {
          logger.info("Successfully refreshed token for WebSocket connection")
          return idToken // Return the same token as EasyAuth handles it via cookies
        }
      }

      return undefined // Refresh failed
    },
  })

  return wsClient.getPromise()
}
